import React, { useEffect, useState } from 'react';
import axios from 'axios';
import styles from './Stores.module.css';
import Layout from '../../Layout/Layout';
import { URL as BASEURL } from '../../AxiosUrlGlobal';

/**
 * Store Management (super admin) — redesigned: green theme, store-health data
 * (products / orders), accurate status, clickable stat filters.
 */
function Stores() {
    const [stores, setStores] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [searchDebounce, setSearchDebounce] = useState('');
    const [statusFilter, setStatusFilter] = useState('all'); // all, active, expired, suspended, trial

    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalStores, setTotalStores] = useState(0);
    const [limit] = useState(10);

    const [stats, setStats] = useState({ totalStores: 0, activeStores: 0, expiredStores: 0, suspendedStores: 0, newThisMonth: 0 });

    // Debounce search
    useEffect(() => {
        const t = setTimeout(() => { setSearchDebounce(searchTerm); setCurrentPage(1); }, 450);
        return () => clearTimeout(t);
    }, [searchTerm]);

    useEffect(() => {
        fetchStores();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, searchDebounce, statusFilter]);

    const fetchStores = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const userId = localStorage.getItem('id');
            const response = await axios.get(`${BASEURL}superadmin/stores/${userId}`, {
                headers: { token },
                params: { page: currentPage, limit, search: searchDebounce, statusFilter },
            });
            if (response.data.success) {
                setStores(response.data.data.stores);
                setStats(response.data.data.stats);
                setTotalPages(response.data.data.pagination.totalPages);
                setTotalStores(response.data.data.pagination.totalStores);
            } else {
                setError(response.data.error || 'Failed to load stores');
            }
            setLoading(false);
        } catch (err) {
            console.error('Error fetching stores:', err);
            setError('Failed to load stores');
            setLoading(false);
        }
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) setCurrentPage(newPage);
    };

    // Click a stat card to filter by status (click active again to clear).
    const selectStatus = (s) => {
        setStatusFilter((prev) => (prev === s ? 'all' : s));
        setCurrentPage(1);
    };

    const STATUS_META = {
        active: { cls: styles.statusActive, label: 'Active' },
        expired: { cls: styles.statusExpired, label: 'Expired' },
        trial: { cls: styles.statusTrial, label: 'Trial' },
        offer: { cls: styles.statusOffer || styles.statusTrial, label: 'Offer' },
        suspended: { cls: styles.statusSuspended || styles.statusExpired, label: 'Suspended' },
    };
    const statusBadge = (s) => {
        const m = STATUS_META[s] || { cls: styles.statusExpired, label: s || 'none' };
        return <span className={`${styles.statusBadge} ${m.cls}`}>{m.label}</span>;
    };

    const storeUrl = (store) => {
        if (store.domain && store.domain.includes('.')) return `https://${store.domain}`;
        if (store.storeName) return `https://${store.storeName}.ecomtechbd.com`;
        return null;
    };
    const isLive = (s) => ['active', 'offer', 'trial'].includes(s.displayStatus);
    const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '—');

    return (
        <Layout>
            <div className={styles.container}>
                {/* ===== Hero header + stat cards (green theme) ===== */}
                <div className={styles.pageHero}>
                    <div className={styles.pageHeroTop}>
                        <div>
                            <h1 className={styles.pageTitle}>Store Management</h1>
                            <p className={styles.pageSubtitle}>All stores on the platform — owner, plan, status and activity</p>
                        </div>
                    </div>

                    <div className={styles.statRow}>
                        <div
                            className={`${styles.statCard} ${styles.statCardHero} ${statusFilter === 'all' ? styles.statCardActive : ''}`}
                            onClick={() => selectStatus('all')} role="button" tabIndex={0}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') selectStatus('all'); }}
                            title="Show all stores"
                        >
                            <span className={styles.statNum}>{Number(stats.totalStores).toLocaleString()}</span>
                            <span className={styles.statLabel}>Total Stores</span>
                        </div>

                        <div
                            className={`${styles.statCard} ${statusFilter === 'active' ? styles.statCardActive : ''}`}
                            onClick={() => selectStatus('active')} role="button" tabIndex={0}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') selectStatus('active'); }}
                            title="Show paying stores"
                        >
                            <span className={styles.statNum}>{stats.activeStores}</span>
                            <span className={styles.statLabel}>Active (paying)</span>
                        </div>

                        <div
                            className={`${styles.statCard} ${statusFilter === 'expired' ? styles.statCardActive : ''}`}
                            onClick={() => selectStatus('expired')} role="button" tabIndex={0}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') selectStatus('expired'); }}
                            title="Show lapsed (expired) paying stores"
                        >
                            <span className={styles.statNum}>{stats.expiredStores}</span>
                            <span className={styles.statLabel}>Expired (lapsed)</span>
                        </div>

                        <div className={`${styles.statCard} ${styles.statCardStatic}`} title="Stores created this month">
                            <span className={styles.statTag}>NEW</span>
                            <span className={styles.statNum}>{stats.newThisMonth}</span>
                            <span className={styles.statLabel}>This Month</span>
                        </div>
                    </div>

                    <div className={styles.pageMeta}>
                        {statusFilter === 'all'
                            ? <><i className="fas fa-hand-pointer"></i> Click a card to filter by status</>
                            : <><i className="fas fa-filter"></i> Showing <strong>{STATUS_META[statusFilter]?.label || statusFilter}</strong> stores ({totalStores})
                                <button type="button" className={styles.clearFilterBtn} onClick={() => selectStatus('all')}>✕ Clear</button></>}
                    </div>
                </div>

                {/* ===== Search + status dropdown ===== */}
                <div className={styles.filtersSection}>
                    <div className={styles.filtersRow}>
                        <div className={styles.searchBox}>
                            <i className="fas fa-search"></i>
                            <input
                                type="text"
                                placeholder="Search by store, domain, owner, or mobile..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <div className={styles.filterGroup}>
                            <label>Status:</label>
                            <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}>
                                <option value="all">All</option>
                                <option value="active">Active (paying)</option>
                                <option value="expired">Expired (lapsed)</option>
                                <option value="suspended">Suspended (unpaid)</option>
                                <option value="trial">Trial (legacy)</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* ===== Stores table ===== */}
                <div className={styles.tableContainer}>
                    <table className={styles.storesTable}>
                        <thead>
                            <tr>
                                <th>Store</th>
                                <th>Owner</th>
                                <th>Plan</th>
                                <th>Status</th>
                                <th className={styles.center}>Products</th>
                                <th className={styles.center}>Orders</th>
                                <th>Created</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="8" className={styles.noData}><i className="fas fa-spinner fa-spin"></i><div>Loading...</div></td></tr>
                            ) : stores.length === 0 ? (
                                <tr><td colSpan="8" className={styles.noData}><i className="fas fa-inbox"></i><div>No stores found</div></td></tr>
                            ) : (
                                stores.map((store) => {
                                    const url = storeUrl(store);
                                    return (
                                        <tr key={store._id}>
                                            <td>
                                                <div className={styles.storeCell}>
                                                    <span className={`${styles.liveDot} ${isLive(store) ? styles.liveOn : styles.liveOff}`} title={isLive(store) ? 'Live' : 'Offline'}></span>
                                                    <div>
                                                        <div className={styles.storeName}>{store.storeName || store.domain || 'Unnamed'}</div>
                                                        {url && (
                                                            <a href={url} target="_blank" rel="noopener noreferrer" className={styles.domainLink}>
                                                                {url.replace('https://', '')} <i className="fas fa-external-link-alt"></i>
                                                            </a>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className={styles.ownerName}>{store.userName || 'N/A'}</div>
                                                <div className={styles.ownerMobile}>{store.mobile || '—'}</div>
                                            </td>
                                            <td>
                                                <span className={styles.packageBadge}>{store.currentPackageType || 'custom'}</span>
                                                <div className={styles.planAmount}>৳{store.packageAmount || 0}</div>
                                            </td>
                                            <td>{statusBadge(store.displayStatus)}</td>
                                            <td className={styles.center}>
                                                <span className={styles.metricPill}><i className="fas fa-box"></i> {store.productsCount ?? 0}</span>
                                            </td>
                                            <td className={styles.center}>
                                                <span className={`${styles.metricPill} ${(store.ordersCount > 0) ? styles.metricHot : ''}`}><i className="fas fa-shopping-cart"></i> {store.ordersCount ?? 0}</span>
                                            </td>
                                            <td>{fmtDate(store.createdAt)}</td>
                                            <td>
                                                <div className={styles.actions}>
                                                    {url && (
                                                        <a className={styles.viewBtn} href={url} target="_blank" rel="noopener noreferrer" title="Visit store">
                                                            <i className="fas fa-store"></i>
                                                        </a>
                                                    )}
                                                    <button className={styles.editBtn} title="View owner"
                                                        onClick={() => window.open(`/superadmin/users?search=${store.mobile || ''}`, '_blank')}>
                                                        <i className="fas fa-user"></i>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                {/* ===== Pagination ===== */}
                <div className={styles.pagination}>
                    <div className={styles.paginationInfo}>
                        <p>
                            Showing {stores.length > 0 ? ((currentPage - 1) * limit) + 1 : 0} to{' '}
                            {Math.min(currentPage * limit, totalStores)} of {totalStores} stores
                        </p>
                    </div>
                    <div className={styles.paginationControls}>
                        <button onClick={() => handlePageChange(1)} disabled={currentPage === 1} className={styles.paginationBtn}><i className="fas fa-angle-double-left"></i></button>
                        <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className={styles.paginationBtn}><i className="fas fa-angle-left"></i></button>
                        <div className={styles.pageNumbers}>
                            {Array.from({ length: totalPages }, (_, i) => i + 1)
                                .filter(page => page === 1 || page === totalPages || (page >= currentPage - 2 && page <= currentPage + 2))
                                .map((page, index, array) => {
                                    const prevPage = array[index - 1];
                                    const showEllipsis = prevPage && page - prevPage > 1;
                                    return (
                                        <React.Fragment key={page}>
                                            {showEllipsis && <span className={styles.ellipsis}>...</span>}
                                            <button onClick={() => handlePageChange(page)} className={`${styles.pageBtn} ${currentPage === page ? styles.activePage : ''}`}>{page}</button>
                                        </React.Fragment>
                                    );
                                })}
                        </div>
                        <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className={styles.paginationBtn}><i className="fas fa-angle-right"></i></button>
                        <button onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} className={styles.paginationBtn}><i className="fas fa-angle-double-right"></i></button>
                    </div>
                </div>
            </div>
        </Layout>
    );
}

export default Stores;
